import {
  ESdChangeReason,
  IOpenAIUserInterfaceValidationAnalysisError,
  OpenAIUserInterfaceValidationRequestDTO,
  ViewPart
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Key, MouseEvent, ReactNode } from "react";
import { OpenAIService } from "../../../../services/openai-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import SparklesSvg from "../../../../svgs/small-icons/sparkles.svg";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu, {
  type KuikaDropdownMenuItemType
} from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import "./audit-window.scss";

enum EAuditItemType {
  Tip,
  Error
}

interface IAuditItem {
  elementId?: string;
  screenId?: string;
  elementFriendlyName?: string;
  screenName?: string;
  title?: string;
  description: string;
  type: EAuditItemType;
}

interface IAuditWindowProps {}

interface IAuditWindowState {
  isSearchActive: boolean;
  searchText: string;
  selectedTipIndex: number;
  selectedValidationIndex: number;
  activePanels: string[];
  listType?: string;
  isLoading?: boolean;
}

class AuditWindow extends PureSdComponent<IAuditWindowProps & IWithSDProps, IAuditWindowState> {
  constructor(props) {
    super(props);
    this.state = {
      isSearchActive: false,
      searchText: "",
      selectedTipIndex: -1,
      selectedValidationIndex: -1,
      activePanels: [],
      listType: "project",
      isLoading: false
    };
  }

  handleSearchInputChange = (searchText: string): void => {
    this.setState({ searchText });
    this.props.sd.setSearchTexts(ViewPart.Audit, searchText);
  };

  deleteSearchText = (): void => {
    this.props.sd.setSearchTexts(ViewPart.Audit, "");
    this.setState({ searchText: "", isSearchActive: false });
  };

  requestScreenAudit = async () => {
    const payload: OpenAIUserInterfaceValidationRequestDTO = {
      projectId: this.props.sd.project.id,
      projectType: this.props.sd.project.projectType,
      appDescription: this.props.sd.project.name,
      screenId: this.props.sd.sdId
    };
    const res = await OpenAIService.UiAnalysisForScreen(payload);
    this.props.sd.setScreenAuditErrors(res.data.errors);
  };

  requestProjectAudit = async () => {
    const payload: OpenAIUserInterfaceValidationRequestDTO = {
      projectId: this.props.sd.project.id,
      appDescription: this.props.sd.project.name
    };
    const res = await OpenAIService.UserInterfaceAnalysis(payload);
    this.props.sd.setprojectAuditErrors(res.data.errors);
  };

  requestValidation = async () => {
    try {
      this.setState({ isLoading: true });
      if (this.state.listType === "project") {
        await this.requestProjectAudit();
      } else {
        await this.requestScreenAudit();
      }
      this.setState({ activePanels: ["errors", "runVerification"] });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  renderItem = (item: IAuditItem, isSelected: boolean): ReactNode => {
    const isAITipItem: boolean = item.type === EAuditItemType.Tip;

    return (
      // `antd`'s `placement="rightTop"` is the kit's `position="right"` + `align="start"`, and its
      // `trigger="click"` is the kit default. The hand-rolled close cross the title used to carry is
      // gone: the kit's own close button reports through `onVisibleChange(false)`, which is the very
      // handler that clears the selection. That cross also stopped the click propagating, which the
      // kit's cannot — `isClickInsidePopover` is where the row declines it instead.
      <KuikaPopover
        className="kuika-audit-panel__popover"
        visible={isSelected && !this.state.isLoading}
        onVisibleChange={(visible: boolean): void => {
          if (!visible) this.setState({ selectedTipIndex: -1, selectedValidationIndex: -1 });
        }}
        title={isAITipItem ? "TIP" : "ERROR"}
        position="right"
        align="start"
        content={
          <div className="kuika_auditList__Item__popover">
            <div className="kuika_auditList__Item__popover__body">
              <div>{item.description}</div>
              {item.elementFriendlyName && (
                <div className="kuika_auditList__Item__popover__elementInfo">Element: {item.elementFriendlyName}</div>
              )}
              {item.screenName && (
                <div className="kuika_auditList__Item__popover__elementInfo">Screen Name: {item.screenName}</div>
              )}
            </div>
            {item.elementId && (
              <div className="kuika_auditList__Item__popover__footer">
                <KuikaButton
                  type="text"
                  className="kuika_auditList__Item__popover__select-item-btn"
                  onClick={() => {
                    const selectElementFunction = () => {
                      this.props.sd.setSelectedDisplayItem(item.elementId ?? "-1");
                    };
                    if (this.props.sd.sdId !== item.screenId) {
                      this.props.sd.loadScreen(item.screenId, false, false, selectElementFunction);
                      return;
                    }
                    selectElementFunction();
                  }}
                >
                  <span>SELECT ELEMENT</span>
                  <KuikaSvg type="arrows" name="arrow-right" width={12} height={12} />
                </KuikaButton>
              </div>
            )}
          </div>
        }
      >
        <div
          className={classNames("kuika_auditList_name", "kuika-audit-panel__row", {
            "kuika_auditList_name-selected": isSelected
          })}
        >
          <SparklesSvg
            className={classNames("kuika-audit-panel__row-glyph", {
              "kuika-audit-panel__row-glyph--hidden": !isAITipItem
            })}
          />
          <span className="kuika-audit-panel__row-text" title={item.description}>
            <KuikaOverflowText>{item.description}</KuikaOverflowText>
          </span>
        </div>
      </KuikaPopover>
    );
  };

  getPanelDropdown = (): ReactNode => {
    const menu: KuikaDropdownMenuItemType[] = [
      {
        label: "Run Verification",
        icon: <KuikaSvg width={16} height={16} name="refresh" type="common" />,
        onClick: (): void => {
          this.requestValidation();
        }
      }
    ];

    return (
      <KuikaButton
        data-testid="runVerificationDotMenuButton"
        onClick={(e) => e.stopPropagation()}
        isIcon
        type="tertiary"
        color="neutral"
      >
        <KuikaDropdownMenu options={menu} overlayClassName="kuika-screens__dropdown">
          <KuikaSvg width={16} height={16} name="dots" />
        </KuikaDropdownMenu>
      </KuikaButton>
    );
  };

  /**
   * The bubble is portaled but is still a React child of the row, so a click inside it reaches this
   * handler and would re-select the row the bubble's own close button had just deselected — the
   * popover would shut and reopen on the same click. The hand-rolled close cross this card used to
   * draw stopped propagation itself for exactly that reason; the kit's close button lives in the
   * popover's title bar, which the `content` prop cannot reach, so the row declines the click here.
   */
  private isClickInsidePopover = (event: MouseEvent<HTMLDivElement>): boolean => {
    const target: Element | null = event.target as Element | null;
    return !!target?.closest?.(".kuika-popover");
  };

  /** Matches the search box the same way both lists do: accent-folded, case-insensitive, substring. */
  private matchesSearch = (text: string): boolean => {
    const needle: string = KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase();
    return text.toLowerCase().indexOf(needle) !== -1;
  };

  private renderTipItems = (auditErrors: IOpenAIUserInterfaceValidationAnalysisError[]): ReactNode => {
    if (auditErrors.length === 0) {
      return <KuikaSmallText className="kuika-audit-panel__empty">No Suggestions</KuikaSmallText>;
    }

    return auditErrors
      ?.filter((item) => this.matchesSearch(item.suggestion) && item.suggestion?.length > 0)
      .map((item, i: number) => {
        const isSelected: boolean = this.state.selectedTipIndex === i;
        return (
          <div
            className={classNames("kuika_auditList__Item ai_tip_item", OVERFLOW_REVEAL_HOST_CLASS, {
              "kuika_auditList__Item-selected": isSelected
            })}
            style={
              isSelected ? { cursor: "unset" } : { left: "auto !important", top: "auto !important", position: "static" }
            }
            key={i}
            id={`btn_ai-tip_${i}`}
            data-testid="auditAiTipItem"
            onClick={(event: MouseEvent<HTMLDivElement>) => {
              if (this.isClickInsidePopover(event)) return;
              this.setState({ selectedTipIndex: i, selectedValidationIndex: -1 });
            }}
          >
            <div className="site-dropdown-context-menu kuika_auditList__Item__dropdown">
              {this.renderItem({ description: item.suggestion, type: EAuditItemType.Tip }, isSelected)}
            </div>
          </div>
        );
      });
  };

  private renderErrorItems = (auditErrors: IOpenAIUserInterfaceValidationAnalysisError[]): ReactNode => {
    if (auditErrors.length === 0) {
      return <KuikaSmallText className="kuika-audit-panel__empty">No Errors</KuikaSmallText>;
    }

    return auditErrors
      ?.filter((x) => this.matchesSearch(x.error))
      .map((item, i: number) => {
        const isSelected: boolean = this.state.selectedValidationIndex === i;
        return (
          <div
            className={classNames("kuika_auditList__Item", OVERFLOW_REVEAL_HOST_CLASS, {
              "kuika_auditList__Item-selected": isSelected
            })}
            style={
              isSelected ? { cursor: "unset" } : { left: "auto !important", top: "auto !important", position: "static" }
            }
            key={i}
            id={`btn_audit_error_${i}`}
            data-testid="auditErrorItem"
            onClick={(event: MouseEvent<HTMLDivElement>) => {
              if (this.isClickInsidePopover(event)) return;
              this.setState({ selectedValidationIndex: i, selectedTipIndex: -1 });
            }}
          >
            <div className="site-dropdown-context-menu kuika_auditList__Item__dropdown">
              {this.renderItem(
                {
                  elementFriendlyName: item.elementFriendlyName,
                  screenName: item.screenName,
                  elementId: item.elementId,
                  screenId: item.screenId,
                  description: item.error,
                  type: EAuditItemType.Error
                },
                isSelected
              )}
            </div>
          </div>
        );
      });
  };

  render(): ReactNode {
    const auditErrors: IOpenAIUserInterfaceValidationAnalysisError[] =
      this.state.listType === "project" ? this.props.sd.projectAuditErrors : this.props.sd.screenAuditErrors;
    // The shell's shared rule (first-load-skeletons): the list in outline only while it has nothing
    // to show. A verification over findings already listed keeps them on screen — the panel used
    // to dim everything, its own controls included, for either.
    const isAuditLoading: boolean = !!this.state.isLoading || this.props.sd.isProjectLoaded !== true;
    const isPlaceholder: boolean =
      resolveLoadingPresentation(isAuditLoading, (auditErrors?.length ?? 0) > 0) === "skeleton";

    return (
      <div className="kuika_topPanel" style={{ width: "100%" }}>
        <SearchablePanelHeader
          searchText={this.state.searchText}
          onSearchTextChange={this.handleSearchInputChange}
          onSearchClearCallback={this.deleteSearchText}
          viewPart={ViewPart.Audit}
          panelName="Audit"
          isSearchActive={this.state.isSearchActive}
          setSearchActive={(value: boolean) => this.setState({ isSearchActive: value })}
        />
        {/* 85px of shell chrome + this panel's own 16px inset (was 130px, which also
            reserved the removed 24px footer). */}
        <div style={{ overflowY: "auto", height: "calc(100vh - 101px - var(--panel-strip-growth, 0px))" }}>
          <div className="kuika-audit-panel__scope">
            <KuikaTabs
              className="kuika-audit-panel__scope-tabs"
              value={this.state.listType}
              options={[
                { label: "Project", value: "project", dataTestId: "auditProjectTabButton" },
                { label: "Screen", value: "screen", dataTestId: "auditScreenTabButton" }
              ]}
              onChange={(value: string): void => {
                this.setState({ listType: value });
              }}
            />
          </div>
          {isPlaceholder ? (
            <KuikaSkeletonRegion label="Loading audit findings" className="kuika-audit-panel__skeleton">
              <KuikaSkeletonList rows={6} withIcon lines={2} />
            </KuikaSkeletonRegion>
          ) : (
            <KuikaCollapsePanel
              className="kuika-audit-panel__collapse"
              activeKey={this.state.searchText.length > 0 ? ["errors"] : this.state.activePanels}
              onChange={(keys: Key[]): void => {
                this.setState({ activePanels: keys.map(String) });
              }}
              items={[
                {
                  key: "runVerification",
                  label: "Run Verification",
                  extra: this.getPanelDropdown(),
                  children: this.renderTipItems(auditErrors)
                },
                {
                  key: "errors",
                  label: "Errors",
                  children: this.renderErrorItems(auditErrors)
                }
              ]}
            />
          )}
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const auditWindow = withSD<IAuditWindowProps>(AuditWindow);

export { auditWindow as AuditWindow };
